import { JournalRegister } from '../components/JournalRegister';
import { useEffect, useState } from 'react';
import { UserManagementPanel, RoleManagementPanel } from 'auth-sso-kit/client';
import type { UserManagementPanelLabels } from 'auth-sso-kit/client';
import { AuditLogViewer } from 'audit-log-kit/client/index.js';
import type { AuditLogViewerLabels } from 'audit-log-kit/client/index.js';
import { NotificationPreferences } from 'notification-kit/client/index.js';
import type { NotificationPreferencesLabels } from 'notification-kit/client/index.js';
import { useT } from '../i18n';
import { LogoSettings } from '../components/LogoSettings';
import { AccentSettings } from '../components/AccentSettings';
import { CardVersionsPanel } from '../components/CardVersionsPanel';
import { DevDataPanel } from '../components/DevDataPanel';
import { api } from '../api/client';

type Tab = 'users' | 'roles' | 'cards' | 'audit' | 'notifications' | 'branding' | 'devdata' | 'journal';

export function AdminPage() {
  const { t } = useT();
  const [tab, setTab] = useState<Tab>('users');
  // Dev only: the server says whether "reload from prod" exists here (never on prod).
  const [canRefresh, setCanRefresh] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<string | null>(null);
  useEffect(() => {
    api.get<Record<string, string>>('/api/admin/settings').then((s) => {
      setCanRefresh(s.canRefreshFromProd === 'true');
      setLastRefreshed(s.dev_last_refreshed_from_prod ?? null);
    }).catch(() => {});
  }, []);

  const userLabels: UserManagementPanelLabels = {
    regModeTitle: t('users.regMode.title'),
    regModeOpen: t('users.regMode.open'),
    regModeAllowlist: t('users.regMode.allowlist'),
    regModeOpenHelp: t('users.regMode.openHelp'),
    regModeAllowlistHelp: t('users.regMode.allowlistHelp'),
    allowlistTitle: t('users.allowlist.title'),
    allowlistNotEnforced: t('users.allowlist.notEnforced'),
    allowlistHelpEnforced: t('users.allowlist.helpEnforced'),
    allowlistHelpNotEnforced: t('users.allowlist.helpNotEnforced'),
    emailPlaceholder: t('users.emailPlaceholder'),
    namePlaceholder: t('users.namePlaceholder'),
    addToAllowlist: t('users.addToAllowlist'),
    createAccount: t('users.createAccount'),
    errValidEmailRequired: t('users.errValidEmail'),
    colEmail: t('users.col.email'),
    colStatus: t('users.col.status'),
    colAdded: t('users.col.added'),
    statusRegistered: t('users.status.registered'),
    statusPending: t('users.status.pending'),
    impersonate: t('users.impersonate'),
    remove: t('users.remove'),
    confirmRemoveEmail: t('users.confirmRemoveEmail'),
    userMgmtTitle: t('users.mgmt.title'),
    colUser: t('users.col.user'),
    colRole: t('users.col.role'),
    colJoined: t('users.col.joined'),
    badgeAdmin: t('users.badge.admin'),
    badgeUser: t('users.badge.user'),
    removeAdmin: t('users.removeAdmin'),
    makeAdmin: t('users.makeAdmin'),
    rolesButton: t('users.roles'),
    resetPassword: t('users.resetPassword'),
    deleteButton: t('users.delete'),
    confirmDeleteUser: t('users.confirmDelete'),
    newPasswordPlaceholder: t('users.newPasswordPlaceholder'),
    setButton: t('users.set'),
    saveRoles: t('users.saveRoles'),
    errPasswordMin: t('users.errPasswordMin'),
    passwordResetDone: t('users.passwordResetDone'),
  };

  const auditLabels: AuditLogViewerLabels = {
    allActions: t('audit.allActions'),
    resourceTypePlaceholder: t('audit.resourceType'),
    startDateTitle: t('audit.startDate'),
    endDateTitle: t('audit.endDate'),
    searchPlaceholder: t('audit.searchPlaceholder'),
    searchButton: t('audit.search'),
    clearButton: t('audit.clear'),
    colTime: t('audit.col.time'),
    colUser: t('audit.col.user'),
    colAction: t('audit.col.action'),
    colResourceType: t('audit.col.resourceType'),
    colResourceId: t('audit.col.resourceId'),
    colDetails: t('audit.col.details'),
    colIp: t('audit.col.ip'),
    loading: t('audit.loading'),
    empty: t('audit.empty'),
    loadFailed: t('audit.loadFailed'),
    clickToExpand: t('audit.clickToExpand'),
    totalEntries: t('audit.totalEntries'),
    pageOf: t('audit.pageOf'),
    previous: t('audit.prev'),
    next: t('audit.next'),
  };

  const notifLabels: NotificationPreferencesLabels = {
    title: t('notif.title'),
    loading: t('notif.loading'),
    sponsorBadge: t('notif.sponsorBadge'),
    allNotifications: t('notif.all'),
    sponsoredOnly: t('notif.sponsoredOnly'),
  };

  return (
    <div>
      <div className="page-header">
        <h1 style={{ margin: 0 }}>{t('admin.title')}</h1>
      </div>

      <div className="tabs" style={{ marginBottom: 20 }}>
        <button className={`tab ${tab === 'users' ? 'tab--active' : ''}`} onClick={() => setTab('users')}>{t('admin.tab.users')}</button>
        <button className={`tab ${tab === 'roles' ? 'tab--active' : ''}`} onClick={() => setTab('roles')}>{t('admin.tab.roles')}</button>
        <button className={`tab ${tab === 'cards' ? 'tab--active' : ''}`} onClick={() => setTab('cards')}>{t('admin.tab.cards')}</button>
        <button className={`tab ${tab === 'journal' ? 'tab--active' : ''}`} onClick={() => setTab('journal')}>Reprise des journaux</button>
        <button className={`tab ${tab === 'audit' ? 'tab--active' : ''}`} onClick={() => setTab('audit')}>{t('admin.tab.audit')}</button>
        <button className={`tab ${tab === 'notifications' ? 'tab--active' : ''}`} onClick={() => setTab('notifications')}>{t('admin.tab.notifications')}</button>
        <button className={`tab ${tab === 'branding' ? 'tab--active' : ''}`} onClick={() => setTab('branding')}>{t('admin.tab.branding')}</button>
        {canRefresh && (
          <button className={`tab ${tab === 'devdata' ? 'tab--active' : ''}`} onClick={() => setTab('devdata')}>{t('admin.tab.devdata')}</button>
        )}
      </div>

      {/* Pending-only allowlist: registered people already appear in the user
          table below, so the invite list shows only open invitations. */}
      {tab === 'users' && <UserManagementPanel labels={userLabels} allowlistPendingOnly />}
      {tab === 'roles' && <RoleManagementPanel />}
      {tab === 'journal' && <JournalRegister />}
      {tab === 'cards' && <CardVersionsPanel />}
      {tab === 'audit' && <AuditLogViewer labels={auditLabels} />}
      {tab === 'notifications' && <NotificationPreferences labels={notifLabels} />}
      {tab === 'branding' && <><LogoSettings /><AccentSettings /></>}
      {tab === 'devdata' && canRefresh && <DevDataPanel lastRefreshed={lastRefreshed} onRefreshed={setLastRefreshed} />}
    </div>
  );
}
